import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModalHistory } from './useModalHistory';

interface AuthDialogProps {
  title: string;
  closeLabel: string;
  className: string;
  onClose: () => void;
  initialFocusRef: React.RefObject<HTMLElement | null>;
  focusKey?: string;
  opener?: HTMLElement | null;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/** Authentication owns its modal surface; the browser contains focus and makes the page inert. */
export const AuthDialog: React.FC<AuthDialogProps> = ({
  title, closeLabel, className, onClose, initialFocusRef, children, footer, focusKey, opener: originalOpener,
}) => {
  const titleId = useId();
  useModalHistory(true, onClose);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pointerStartedOutside = useRef(false);

  useEffect(() => {
    const opener = originalOpener ?? document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current!;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      const candidates = [opener, ...['.common-header-toggle', '.auth-status-link', '.common-header > a']
        .map(selector => document.querySelector<HTMLElement>(selector))];
      const target = candidates.find(element => element !== document.body && element !== document.documentElement && element?.isConnected
        && element.getClientRects().length > 0
        && getComputedStyle(element).visibility !== 'hidden');
      target?.focus({ preventScroll: true });
    };
    // The modal and its opener live for the entire authentication flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (initialFocusRef.current ?? dialogRef.current?.querySelector<HTMLElement>('h2'))?.focus();
  }, [focusKey, initialFocusRef]);

  const isOutside = (event: React.PointerEvent | React.MouseEvent) => {
    const rect = dialogRef.current!.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right
      || event.clientY < rect.top || event.clientY > rect.bottom;
  };

  const containTab = (event: React.KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Tab') return;
    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled):not([type="hidden"]), a[href], select:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
    )).filter(element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  return createPortal(
    <dialog ref={dialogRef} className={`auth-dialog ${className}`} aria-labelledby={titleId}
      aria-modal="true" onKeyDown={containTab} onCancel={event => { event.preventDefault(); onClose(); }}
      onPointerDown={event => { pointerStartedOutside.current = event.target === event.currentTarget && isOutside(event); }}
      onClick={event => {
        if (pointerStartedOutside.current && event.target === event.currentTarget && isOutside(event)) onClose();
        pointerStartedOutside.current = false;
      }}>
      <header className="auth-dialog-header">
        <h2 id={titleId} tabIndex={-1}>{title}</h2>
        <button type="button" className="auth-dialog-close" aria-label={closeLabel} onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
      </header>
      <div className="auth-dialog-body">{children}</div>
      {footer && <footer className="auth-dialog-footer">{footer}</footer>}
    </dialog>, document.body
  );
};
